/* eslint-disable quotes */
'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ApplyJobForm from '../apply-job-form';

export default function PropertyConsultantPosition() {
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const responsibilities = [
    {
      title: 'Sales & Client Engagement',
      items: [
        'Work on company-provided leads and databases',
        "Maintain daily 'hot communication' with prospects",
        'Build strong client confidence in Dubai investments',
        'Arrange and attend meetings and viewings',
        'Collaborate with developer teams and closers',
        'Serve as a true consultant to clients',
      ],
    },
    {
      title: 'Administration & Reporting',
      items: [
        'Maintain CRM records with accuracy',
        'Prepare weekly activity reports',
        'Ensure compliance with RERA standards',
        'Coordinate with admin teams',
        'Manage timely client follow-ups',
      ],
    },
    {
      title: 'Content Creation & Brand Building',
      items: [
        'Produce a minimum of 15 videos/social content monthly',
        'Showcase properties and market insights',
        'Uphold X-Space brand identity',
        'Collaborate with the marketing team',
      ],
    },
  ];

  const kpis = [
    'Lead Engagement: 50% of assigned leads',
    'Conversions: 30% of leads into meetings',
    'Administration: 100% CRM updates',
    'Content Creation: 15 posts monthly',
    'Closures: 5% of assigned leads',
    'Training & Attendance: 100%',
  ];

  const qualifications = [
    'Experience in real estate sales or telesales preferred',
    'Strong communication & relationship-building skills',
    'Ability to create social media content (video/posts)',
    'Strong administrative skills (CRM, reporting)',
    'Proficiency in MS Office, CRM, social media',
    'Fluent in English (other languages are a plus)',
    'Ambitious, target-driven, and disciplined',
  ];

  return (
    <div className='bg-white dark:bg-gray-950 pt-28 pb-20'>
      <div className='container mx-auto px-4'>
        <Link
          href='/careers'
          className='inline-flex items-center text-[#fe2a53] dark:text-[#38f5ef] hover:underline mb-8'
        >
          <ArrowLeft className='w-4 h-4 mr-2' />
          Back to Careers
        </Link>

        <div className='max-w-5xl mx-auto'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className='text-center mb-12'>
              <h1 className='text-3xl md:text-4xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                PROPERTY CONSULTANT
              </h1>
              <h2 className='text-2xl font-semibold text-[#fe2a53] dark:text-[#38f5ef]'>
                JOB DESCRIPTION
              </h2>
            </div>

            {/* About & Role */}
            <div className='space-y-8 mb-12'>
              <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-6 md:p-10'>
                <h3 className='text-xl md:text-2xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                  About X-Space Real Estate
                </h3>
                <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                  X-Space Real Estate is a leading property brokerage in Dubai,
                  driven by innovation, professionalism, and strong client
                  trust. With a portfolio covering prime residential and
                  investment opportunities, we are committed to guiding clients
                  in making confident real estate decisions. We are introducing
                  the Property Consultant position – a full-time in-house sales
                  role designed to maximize company-provided leads, drive client
                  engagement, strengthen our digital presence, and uphold the
                  X-Space brand.
                </p>
              </div>

              <div className='bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-6 md:p-10'>
                <h3 className='text-xl md:text-2xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                  Role Overview
                </h3>
                <p className='text-gray-700 dark:text-gray-300 leading-relaxed'>
                  The Property Consultant is a full-time employee of X-Space
                  Real Estate (not a freelance broker). This role focuses on
                  generating company leads, utilizing in-house databases, and
                  creating content for brand growth. Success is measured by
                  KPIs, with opportunities to earn a competitive basic salary up
                  to AED 5,000/month + commission of up to 30%.
                </p>
              </div>
            </div>

            {/* Responsibilities */}
            <div className='grid gap-8 mb-12 md:grid-cols-3'>
              {responsibilities.map((section, idx) => (
                <div
                  key={idx}
                  className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 flex flex-col h-full'
                >
                  <h4 className='text-lg font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-3'>
                    {section.title}
                  </h4>
                  <ul className='space-y-2 text-gray-700 dark:text-gray-300 flex-1'>
                    {section.items.map((item, i) => (
                      <li key={i} className='flex items-start'>
                        <CheckCircle className='w-5 h-5 text-[#fe2a53] dark:text-[#38f5ef] mt-1 mr-2 flex-shrink-0' />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* KPIs */}
            <div className='bg-gradient-to-r from-[#fe2a53]/30 to-[#fe2a53]/10 dark:from-[#38f5ef]/30 dark:to-[#38f5ef]/10 rounded-3xl shadow-lg p-6 md:p-10 mb-12'>
              <h3 className='text-xl md:text-2xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4 text-center'>
                Key Performance Indicators (KPIs)
              </h3>
              <ul className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-gray-700 dark:text-gray-300'>
                {kpis.map((kpi, idx) => (
                  <li
                    key={idx}
                    className='flex items-center text-lg font-semibold'
                  >
                    <CheckCircle className='w-5 h-5 text-[#fe2a53] dark:text-[#38f5ef] mr-2 flex-shrink-0' />
                    {kpi}
                  </li>
                ))}
              </ul>
            </div>

            {/* Compensation & Qualifications */}
            <div className='grid gap-8 md:grid-cols-2 mb-12'>
              <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6'>
                <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                  Compensation & Growth
                </h3>
                <ul className='space-y-2 text-gray-700 dark:text-gray-300'>
                  <li>
                    Basic Salary: AED 3,000 – AED 5,000 (KPI-based), possibility
                    to increase with performance
                  </li>
                  <li>Commission: 10% – 30% on closed deals</li>
                  <li>
                    Growth Path: Progression to Senior Consultant or Team Leader
                  </li>
                </ul>
              </div>
              <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6'>
                <h3 className='text-xl font-bold text-[#fe2a53] dark:text-[#38f5ef] mb-4'>
                  Qualifications & Requirements
                </h3>
                <ul className='space-y-2 text-gray-700 dark:text-gray-300'>
                  {qualifications.map((q, i) => (
                    <li key={i} className='flex items-start'>
                      <CheckCircle className='w-5 h-5 text-[#fe2a53] dark:text-[#38f5ef] mt-1 mr-2 flex-shrink-0' />
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Apply Button */}
            {/* <div className='text-center'>
              <Button
                onClick={() => setShowForm(true)}
                className='bg-gradient-to-r from-[#fe2a53] to-[#ff6b8a] dark:from-[#38f5ef] dark:to-[#3ce2e2] hover:opacity-90 text-white px-8 py-6 text-lg rounded-full transition-all duration-300'
              >
                Apply for this Position
              </Button>
            </div> */}

            <div className='text-center'>
              <Button
                onClick={() => setShowForm(true)}
                className='bg-gradient-to-r bg-primarycolor dark:bg-secondarycolor hover:bg-secondarycolor dark:hover:bg-primarycolor text-white hover:text-black dark:hover:text-white px-8 py-6 text-lg rounded-full'
              >
                Apply for this Position
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {showForm && (
        <ApplyJobForm
          onClose={() => setShowForm(false)}
          jobTitle='Property Consultant'
        />
      )}
    </div>
  );
}
